import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Eye, PencilSimple, Trash, CheckCircle, ArrowCounterClockwise, Package } from '@phosphor-icons/react';
import { API, CONDITION_LABELS, photoUrl, formatPrice } from '../utils/marketplace';

export default function MarketplaceMyListings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counterFor, setCounterFor] = useState(null);
  const [counterAmount, setCounterAmount] = useState('');

  const fetchOffers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/offers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOffers(res.data.received || []);
    } catch { /* ignore */ }
  }, [token]);

  const respondOffer = async (offerId, action, amount) => {
    try {
      await axios.post(`${API}/api/marketplace/offers/${offerId}/respond`,
        { action, ...(action === 'counter' ? { counter_amount: Number(amount) } : {}) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === 'accept'
        ? 'Offer accepted — buyer notified with a checkout link!'
        : action === 'counter' ? 'Counter-offer sent to the buyer!' : 'Offer declined');
      setCounterFor(null);
      setCounterAmount('');
      fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const fetchMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/my/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setListings(res.data.listings);
    } catch {
      toast.error('Could not load your listings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate('/login?redirect=/marketplace/my-listings'); return; }
    fetchMine();
    fetchOffers();
  }, [token, navigate, fetchMine, fetchOffers]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/marketplace/listings/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(status === 'sold' ? 'Marked as sold!' : 'Listing reactivated');
      fetchMine();
    } catch {
      toast.error('Update failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this listing permanently?')) return;
    try {
      await axios.delete(`${API}/api/marketplace/listings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Listing deleted');
      setListings((ls) => ls.filter((l) => l._id !== id));
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="my-listings-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl md:text-3xl font-black uppercase text-neutral-900 dark:text-white">My Listings</h1>
          <div className="flex gap-2">
            <Link
              to="/marketplace/orders"
              className="inline-flex items-center gap-1.5 bg-[#2B4C6F] text-white font-black uppercase text-xs px-4 py-2.5 border-2 border-black shadow-[3px_3px_0px_#000]"
              data-testid="my-listings-orders-link"
            >
              Orders
            </Link>
            <button
              onClick={() => navigate('/marketplace/new')}
              className="inline-flex items-center gap-1.5 bg-[#D95800] text-white font-black uppercase text-xs px-4 py-2.5 border-2 border-black shadow-[3px_3px_0px_#000]"
              data-testid="my-listings-new-btn"
            >
              <Plus size={14} weight="bold" /> New Listing
            </button>
          </div>
        </div>

        {offers.length > 0 && (
          <div className="mt-5 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-600 rounded-xl p-4" data-testid="pending-offers-section">
            <p className="font-black uppercase text-sm text-emerald-800 dark:text-emerald-300 mb-3">💰 Offers ({offers.length})</p>
            <div className="space-y-2">
              {offers.map((o) => (
                <div key={o._id} className="bg-white dark:bg-neutral-800 border-2 border-black rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`offer-${o._id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-neutral-900 dark:text-white truncate">{o.listing_title}</p>
                    <p className="text-xs text-neutral-500">{o.buyer_name || 'Buyer'} offers <span className="font-black text-emerald-600">{formatPrice(o.amount)}</span> <span className="line-through">{formatPrice(o.asking_price)}</span></p>
                  </div>
                  {o.status === 'countered' ? (
                    <span className="shrink-0 text-[11px] font-black uppercase text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 border border-amber-500 rounded-lg px-3 py-2" data-testid={`countered-badge-${o._id}`}>
                      🔁 Countered at {formatPrice(o.counter_amount)} — waiting on buyer
                    </span>
                  ) : counterFor === o._id ? (
                    <div className="flex gap-2 shrink-0 items-center" data-testid={`counter-form-${o._id}`}>
                      <input
                        type="number" min="1" step="1"
                        value={counterAmount}
                        onChange={(e) => setCounterAmount(e.target.value)}
                        placeholder="Counter $"
                        className="w-28 text-sm px-2 py-2 border-2 border-black rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none"
                        data-testid={`counter-amount-input-${o._id}`}
                      />
                      <button onClick={() => respondOffer(o._id, 'counter', counterAmount)} disabled={!counterAmount} className="bg-amber-500 text-black font-black uppercase text-xs px-3 py-2 border-2 border-black rounded-lg disabled:opacity-50" data-testid={`counter-send-${o._id}`}>Send</button>
                      <button onClick={() => { setCounterFor(null); setCounterAmount(''); }} className="text-xs font-bold text-neutral-500 px-1">✕</button>
                    </div>
                  ) : (
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => respondOffer(o._id, 'accept')} className="bg-emerald-600 text-white font-black uppercase text-xs px-4 py-2 border-2 border-black rounded-lg" data-testid={`accept-offer-${o._id}`}>Accept</button>
                      <button onClick={() => { setCounterFor(o._id); setCounterAmount(''); }} className="bg-amber-500 text-black font-black uppercase text-xs px-3 py-2 border-2 border-black rounded-lg" data-testid={`counter-offer-${o._id}`}>Counter</button>
                      <button onClick={() => respondOffer(o._id, 'decline')} className="bg-white dark:bg-neutral-900 text-red-500 font-black uppercase text-xs px-3 py-2 border-2 border-black rounded-lg" data-testid={`decline-offer-${o._id}`}>Decline</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin w-10 h-10 border-4 border-[#D95800] border-t-transparent rounded-full" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20" data-testid="my-listings-empty">
            <Package size={56} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">No listings yet</p>
            <p className="text-sm text-neutral-500 mt-1">Sell your unused tools or leftover materials.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {listings.map((l) => (
              <div key={l._id} className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-3 flex gap-3 items-center" data-testid={`my-listing-${l._id}`}>
                <Link to={`/marketplace/listing/${l._id}`} className="w-16 h-16 shrink-0 border-2 border-black rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
                  {l.photos?.length > 0
                    ? <img src={photoUrl(l.photos[0])} alt="" className="w-full h-full object-cover" />
                    : <Package size={24} weight="duotone" className="text-neutral-300" />}
                </Link>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link to={`/marketplace/listing/${l._id}`} className="font-bold text-sm text-neutral-900 dark:text-white truncate hover:text-[#D95800]">{l.title}</Link>
                    <span className={`shrink-0 text-[9px] font-black uppercase px-1.5 py-0.5 rounded border border-black text-white ${l.status === 'active' ? 'bg-emerald-500' : l.status === 'sold' ? 'bg-red-500' : 'bg-neutral-500'}`}>
                      {l.status}
                    </span>
                  </div>
                  <p className="text-sm font-black text-[#D95800]">{formatPrice(l.price)}</p>
                  <p className="text-[11px] text-neutral-500 flex items-center gap-2">
                    <span>{CONDITION_LABELS[l.condition]}</span>
                    <span className="flex items-center gap-0.5"><Eye size={11} weight="bold" /> {l.views || 0}</span>
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {l.status === 'active' ? (
                    <button onClick={() => setStatus(l._id, 'sold')} title="Mark sold" className="p-2 bg-emerald-500 text-white border-2 border-black rounded-lg" data-testid={`mark-sold-${l._id}`}>
                      <CheckCircle size={16} weight="bold" />
                    </button>
                  ) : (
                    <button onClick={() => setStatus(l._id, 'active')} title="Relist" className="p-2 bg-[#2B4C6F] text-white border-2 border-black rounded-lg" data-testid={`relist-${l._id}`}>
                      <ArrowCounterClockwise size={16} weight="bold" />
                    </button>
                  )}
                  <button onClick={() => navigate(`/marketplace/new?edit=${l._id}`)} title="Edit" className="p-2 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-2 border-black rounded-lg" data-testid={`edit-${l._id}`}>
                    <PencilSimple size={16} weight="bold" />
                  </button>
                  <button onClick={() => remove(l._id)} title="Delete" className="p-2 bg-red-500 text-white border-2 border-black rounded-lg" data-testid={`delete-${l._id}`}>
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
